import * as Sentry from '@sentry/react';
import { IS_LIVE_PROD, IS_EDITOR_MODE, IS_LOCAL_DEV, SENTRY_REACT_DSN } from 'config/envVariable';

const SENTRY_TRACES_SAMPLE_RATE = process.env.REACT_APP_SENTRY_TRACES_SAMPLE_RATE;

export function initErrorLogService() {
    if (!SENTRY_REACT_DSN) return;

    const DEFAULT_TRACES_SAMPLE_RATE = 0.00001;
    const tracesSampleRate = isNaN(parseFloat(SENTRY_TRACES_SAMPLE_RATE))
        ? DEFAULT_TRACES_SAMPLE_RATE
        : parseFloat(SENTRY_TRACES_SAMPLE_RATE) || DEFAULT_TRACES_SAMPLE_RATE;

    Sentry.init({
        dsn: SENTRY_REACT_DSN,
        environment: IS_LIVE_PROD ? 'production' : IS_LOCAL_DEV ? 'local' : 'dev',
        normalizeDepth: 8,
        maxBreadcrumbs: 80,
        integrations(integrations) {
            return [
                ...integrations.filter(i => i.name !== 'GlobalHandlers'),
                Sentry.browserTracingIntegration()
            ];
        },
        beforeSend(event, hint) {
            const { originalException, captureContext: { extra } = {} } = hint;

            // 允許 captureMessage 事件通過（如 Turnstile-Graceful-Degradation）
            // Sentry v5 的 captureMessage 會將 message string 設為 originalException
            if (!originalException || typeof originalException === 'string') {
                return event;
            }

            // 在編輯器模式下，捕獲主動拋出的錯誤
            if (IS_EDITOR_MODE && originalException.isActivelyCatchErr) {
                return event;
            }

            // 非編輯器模式：過濾 network/chunk load 錯誤，只上報主動捕獲的錯誤
            if (
                isNetworkResourceError(originalException) ||
                isNetworkResourceError(extra) ||
                !originalException.isActivelyCatchErr
            ) {
                return null;
            }
            return event;
        },
        tracesSampleRate
    });
}

/**
 * 將非 Error 物件包裝成 Error 實例
 */
function wrapAsError(obj) {
    const errType = obj?.extraInfo?.errType || obj?.errType || 'UNKNOWN_ERROR';
    const error = new Error(errType);
    error.extraInfo = obj?.extraInfo || obj || {};
    return error;
}

export function sentryCaptureMessage(message, options) {
    try {
        // E2E 測試可觀測性 — 發送 DOM 事件讓測試可以驗證
        // captureMessage 呼叫，不受 Sentry DSN / 初始化狀態影響
        if (typeof window !== 'undefined' && typeof CustomEvent !== 'undefined') {
            window.dispatchEvent(new CustomEvent('sentry:message', { detail: { message } }));
        }
        return Sentry.captureMessage(message, options);
    } catch (error) {
        console.error('Error on sentry captureMessage:', error);
    }
}

export function sentryCaptureException(err) {
    try {
        const error = err instanceof Error ? err : wrapAsError(err);
        const options = {
            extra: { ...(error.extraInfo ?? {}), deviceDate: new Date().toString() }
        };
        error.isActivelyCatchErr = true;

        return Sentry.captureException(error, options);
    } catch (error) {
        console.error('Error on sentry capture:', error);
    }
}

function isChunkLoadError(errObj = {}) {
    const errName = String(errObj.name || '').toLowerCase();
    const errMessage = String(errObj.message || '').toLowerCase();
    return errName === 'chunkloaderror' || errMessage.includes('loading chunk');
}

function isFetchNetworkError(errObj = {}) {
    const errName = String(errObj.name || '').toLowerCase();
    const errMessage = String(errObj.message || '').toLowerCase();
    const isAbortError = errName === 'aborterror';
    const isNetworkTypeError =
        errName === 'typeerror' &&
        [
            'failed to fetch',
            'network request failed',
            'the network connection was lost.',
            'networkerror when attempting to fetch resource.',
            'cancelled',
            '已取消',
            '要求逾時。',
            'the request timed out.',
            '網路連線中斷。',
            'internet 連線已斷開。',
            'internet連線已斷開。',
            '無法完成作業。軟體導致連線失敗',
            '無法連接伺服器。'
        ].some(msg => errMessage === msg);

    return isAbortError || isNetworkTypeError;
}

function isOneOfErrors(checkers, errObj) {
    return checkers.some(checker => checker(errObj));
}

function isNetworkResourceError(errObj = {}) {
    return isOneOfErrors([isChunkLoadError, isFetchNetworkError], errObj);
}
